import { PropsWithChildren, useMemo, useState } from 'react';
import { addYears, subDays } from 'date-fns';
import { TFunction } from 'i18next';
import * as z from 'zod';
import {
  Badge,
  BadgeVariants,
  Button,
  Column,
  ELEVATION,
  EmptyState,
  Form,
  Grid,
  Modal,
  SIZE,
  Stack,
  Switch,
  Text,
  Center,
  useForm,
} from '@quinyx/ui';

import {
  IScriveLicensePeriod,
  ScriveLicensePeriodStatus,
} from '@/api/interfaces/customer';
import {
  useCreateScriveLicensePeriod,
  useDeleteScriveLicensePeriod,
  useGetScriveLicenses,
  useUpdateScriveLicensePeriod,
} from '@/hooks/customer';
import { useTranslate } from '@/hooks/translate';
import { DATE_FORMAT } from '@/constants/time';
import {
  format,
  formatDate,
  formatDateRange,
  formatDateTime,
  newDate,
} from '@/utils/time';

type ScriveLicensesProps = {
  domainId: string;
  isDisabled?: boolean;
};

const createPeriodSchema = (t: TFunction) =>
  z
    .object({
      numLicenses: z.coerce.number().int().min(1),
      dateFrom: z.date(),
      dateTo: z.date(),
    })
    .refine((data) => data.dateTo >= data.dateFrom, {
      message: t('license.scrive.validToAfterFrom'),
      path: ['dateTo'],
    });

const EMPTY_PERIOD_VALUES = {
  numLicenses: undefined,
  dateFrom: undefined,
  dateTo: undefined,
};

type PeriodModalState = {
  open: boolean;
  period: IScriveLicensePeriod | null;
};

type DeleteModalState = {
  open: boolean;
  period: IScriveLicensePeriod | null;
};

const getPeriodFormValues = (period: IScriveLicensePeriod | null) =>
  period
    ? {
        numLicenses: period.numLicenses,
        dateFrom: newDate(period.dateFrom),
        dateTo: newDate(period.dateTo),
      }
    : EMPTY_PERIOD_VALUES;

const buildPeriodPayload = (
  values: z.infer<ReturnType<typeof createPeriodSchema>>,
) => ({
  numLicenses: values.numLicenses,
  dateFrom: format(values.dateFrom, DATE_FORMAT),
  dateTo: format(values.dateTo, DATE_FORMAT),
});

const PERIOD_STATUS_CONFIG: Record<
  ScriveLicensePeriodStatus,
  {
    order: number;
    badgeVariant: BadgeVariants;
    labelKey: string;
    editable: boolean;
  }
> = {
  CURRENT: {
    order: 0,
    badgeVariant: 'displayGreen',
    labelKey: 'license.scrive.statusCurrent',
    editable: true,
  },
  FUTURE: {
    order: 1,
    badgeVariant: 'displayFlamingo',
    labelKey: 'license.scrive.statusFuture',
    editable: true,
  },
  PAST: {
    order: 2,
    badgeVariant: 'displayMonsoon',
    labelKey: 'license.scrive.statusPast',
    editable: false,
  },
  DELETED: {
    order: 3,
    badgeVariant: 'displayRed',
    labelKey: 'license.scrive.statusDeleted',
    editable: false,
  },
};

const compareWithinStatus = (
  a: IScriveLicensePeriod,
  b: IScriveLicensePeriod,
) => {
  switch (a.status) {
    case 'FUTURE':
      return newDate(a.dateFrom).getTime() - newDate(b.dateFrom).getTime();
    case 'PAST':
      return newDate(b.dateTo).getTime() - newDate(a.dateTo).getTime();
    case 'DELETED':
      return (
        newDate(b.deletedAt ?? b.lastEditedAt).getTime() -
        newDate(a.deletedAt ?? a.lastEditedAt).getTime()
      );
    default:
      return 0;
  }
};

const ScrivePeriodBadge = ({
  testId,
  status,
}: {
  testId: string;
  status: ScriveLicensePeriodStatus;
}) => {
  const { t } = useTranslate();
  const { labelKey, badgeVariant } = PERIOD_STATUS_CONFIG[status];

  return (
    <Badge data-test-id={testId} label={t(labelKey)} variant={badgeVariant} />
  );
};

const getScriveColumns = (showDeleted: boolean) =>
  ({
    period: 1,
    licenses: 1,
    validRange: showDeleted ? 2 : 3,
    created: showDeleted ? 2 : 3,
    lastEdited: showDeleted ? 2 : 3,
    deleted: showDeleted ? 3 : undefined,
    actions: 1,
  }) as const;

const PeriodMetadataCell = ({
  timestamp,
  by,
}: {
  timestamp?: string;
  by?: string;
}) => {
  const { t } = useTranslate();

  if (!timestamp || !by) {
    return null;
  }

  return (
    <Stack spacing={SIZE.XS}>
      <Text variant="smallRegular">{formatDateTime(timestamp)}</Text>
      <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
        {t('license.scrive.byUser', { name: by })}
      </Text>
    </Stack>
  );
};

const ScriveTableGrid = ({ children }: PropsWithChildren) => (
  <Grid columns={12} gap={SIZE.S}>
    {children}
  </Grid>
);

const ScrivePeriodRow = ({
  entry,
  showDeleted,
  isDisabled,
  onEdit,
  onDelete,
}: {
  entry: IScriveLicensePeriod;
  showDeleted: boolean;
  isDisabled?: boolean;
  onEdit: (period: IScriveLicensePeriod) => void;
  onDelete: (period: IScriveLicensePeriod) => void;
}) => {
  const { t } = useTranslate();
  const columns = getScriveColumns(showDeleted);

  return (
    <Stack
      p={SIZE.S}
      fullWidth
      backgroundColor={entry.deleted ? 'SurfaceContrastLow' : 'SurfaceMain'}>
      <ScriveTableGrid>
        <Column span={columns.period}>
          <ScrivePeriodBadge
            testId={`scrive-period-badge-${entry.id}`}
            status={entry.status}
          />
        </Column>
        <Column span={columns.licenses}>
          <Text variant="smallRegular">{entry.numLicenses}</Text>
        </Column>
        <Column span={columns.validRange}>
          <Text variant="smallRegular">
            {formatDateRange(entry.dateFrom, entry.dateTo)}
          </Text>
        </Column>
        <Column span={columns.created}>
          <PeriodMetadataCell
            timestamp={entry.createdAt}
            by={entry.createdBy}
          />
        </Column>
        <Column span={columns.lastEdited}>
          <PeriodMetadataCell
            timestamp={entry.lastEditedAt}
            by={entry.lastEditedBy}
          />
        </Column>
        {showDeleted && (
          <Column span={columns.deleted}>
            <PeriodMetadataCell
              timestamp={entry.deletedAt}
              by={entry.deletedBy}
            />
          </Column>
        )}
        <Column span={columns.actions}>
          {PERIOD_STATUS_CONFIG[entry.status].editable && !entry.deleted && (
            <Stack direction="row" spacing={SIZE.S}>
              <Button
                data-test-id={`scrive-license-edit-button-${entry.id}`}
                aria-label={t('license.scrive.editLicense')}
                icon="edit"
                variant="text"
                onClick={() => onEdit(entry)}
                disabled={isDisabled}
              />
              <Button
                data-test-id={`scrive-license-delete-button-${entry.id}`}
                aria-label={t('license.scrive.deleteLicense')}
                icon="delete"
                variant="text"
                color="error"
                onClick={() => onDelete(entry)}
                disabled={isDisabled}
              />
            </Stack>
          )}
        </Column>
      </ScriveTableGrid>
    </Stack>
  );
};

const ScrivePeriodHeader = ({ showDeleted }: { showDeleted: boolean }) => {
  const { t } = useTranslate();
  const columns = getScriveColumns(showDeleted);

  return (
    <ScriveTableGrid>
      <Column span={columns.period}>
        <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
          {t('license.scrive.period')}
        </Text>
      </Column>
      <Column span={columns.licenses}>
        <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
          {t('license.scrive.licenses')}
        </Text>
      </Column>
      <Column span={columns.validRange}>
        <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
          {t('license.scrive.validRange')}
        </Text>
      </Column>
      <Column span={columns.created}>
        <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
          {t('license.scrive.created')}
        </Text>
      </Column>
      <Column span={columns.lastEdited}>
        <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
          {t('license.scrive.lastEdited')}
        </Text>
      </Column>
      {showDeleted && (
        <Column span={columns.deleted}>
          <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
            {t('license.scrive.statusDeleted')}
          </Text>
        </Column>
      )}
      <Column span={columns.actions}>
        <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
          {t('common.actions')}
        </Text>
      </Column>
    </ScriveTableGrid>
  );
};

const sortScriveLicenses = (scriveLicenses: IScriveLicensePeriod[]) =>
  [...scriveLicenses].sort((a, b) => {
    const statusDiff =
      PERIOD_STATUS_CONFIG[a.status].order -
      PERIOD_STATUS_CONFIG[b.status].order;
    return statusDiff || compareWithinStatus(a, b);
  });

const ScriveLicenses = ({
  domainId,
  isDisabled = false,
}: ScriveLicensesProps) => {
  const { t, selectedLanguage } = useTranslate();
  const [periodModal, setPeriodModal] = useState<PeriodModalState>({
    open: false,
    period: null,
  });
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
    open: false,
    period: null,
  });
  const [showDeleted, setShowDeleted] = useState(false);
  const { data: sortedScriveLicenses = [] } = useGetScriveLicenses(domainId, {
    showDeleted,
    select: sortScriveLicenses,
  });
  const { createScriveLicensePeriod, isPending: isCreating } =
    useCreateScriveLicensePeriod();
  const { updateScriveLicensePeriod, isPending: isUpdating } =
    useUpdateScriveLicensePeriod();
  const { deleteScriveLicensePeriod, isPending: isDeleting } =
    useDeleteScriveLicensePeriod();

  const isEditing = periodModal.period !== null;
  const isSubmitting = isCreating || isUpdating;

  const periodSchema = useMemo(() => createPeriodSchema(t), [t]);

  const formMethods = useForm({
    schema: periodSchema,
    defaultValues: EMPTY_PERIOD_VALUES,
  });

  const dayPickerMonthRange = useMemo(
    () => ({
      startMonth: addYears(new Date(), -10),
      endMonth: addYears(new Date(), 10),
    }),
    [],
  );

  const handlePeriodModalOpenChange = (open: boolean) => {
    if (!open) {
      formMethods.reset(EMPTY_PERIOD_VALUES);
      setPeriodModal({ open: false, period: null });
    }
  };

  const handleOpenAddModal = () => {
    formMethods.reset(EMPTY_PERIOD_VALUES);
    setPeriodModal({ open: true, period: null });
  };

  const handleOpenEditModal = (period: IScriveLicensePeriod) => {
    formMethods.reset(getPeriodFormValues(period));
    setPeriodModal({ open: true, period });
  };

  const handleDeleteModalOpenChange = (
    open: boolean,
    period: IScriveLicensePeriod | null = null,
  ) => {
    setDeleteModal({ open, period: open ? period : null });
  };

  const handleDeleteConfirm = () => {
    if (!deleteModal.period) {
      return;
    }

    deleteScriveLicensePeriod([domainId, deleteModal.period.id], {
      onSuccess: () => handleDeleteModalOpenChange(false),
    });
  };

  const handleDateFromChange = (date: Date | null) => {
    if (date && !formMethods.getValues('dateTo')) {
      formMethods.setValue('dateTo', subDays(addYears(date, 1), 1), {
        shouldValidate: true,
      });
    }
  };

  const handlePeriodSubmit = (
    values: z.infer<ReturnType<typeof createPeriodSchema>>,
  ) => {
    const payload = buildPeriodPayload(values);
    const onSuccess = () => handlePeriodModalOpenChange(false);

    if (periodModal.period) {
      updateScriveLicensePeriod([domainId, periodModal.period.id, payload], {
        onSuccess,
      });
      return;
    }

    createScriveLicensePeriod([domainId, payload], { onSuccess });
  };

  return (
    <Stack spacing={SIZE.L}>
      <Stack spacing={SIZE.XS}>
        <Text variant="headlineHeavy">{t('license.scrive.title')}</Text>
        <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
          {t('license.scrive.description')}
        </Text>
      </Stack>

      <Switch
        data-test-id="scrive-license-show-deleted-switch"
        label={t('license.scrive.showDeleted')}
        labelPosition="right"
        checked={showDeleted}
        onChange={(checked) => setShowDeleted(checked.target.value)}
      />
      {sortedScriveLicenses.length > 0 ? (
        <Stack
          border
          borderRadius={SIZE.L}
          backgroundColor="SurfaceMain"
          elevation={ELEVATION.SMOOTH}
          dividers
          fullWidth>
          <Stack
            p={SIZE.S}
            backgroundColor="SurfaceContrastLeast"
            fullWidth={false}>
            <ScrivePeriodHeader showDeleted={showDeleted} />
          </Stack>

          {sortedScriveLicenses.map((entry) => (
            <ScrivePeriodRow
              key={entry.id}
              entry={entry}
              showDeleted={showDeleted}
              isDisabled={isDisabled}
              onEdit={handleOpenEditModal}
              onDelete={(period) => handleDeleteModalOpenChange(true, period)}
            />
          ))}
        </Stack>
      ) : (
        <Center fullWidth py={SIZE.XL}>
          <EmptyState data-test-id="scrive-licenses-empty-state">
            <EmptyState.Image>
              <EmptyState.Image.GetStartedOnWhite />
            </EmptyState.Image>
            <EmptyState.Content>
              <EmptyState.Headline>
                {t('license.scrive.noPeriods')}
              </EmptyState.Headline>
              <EmptyState.Body>
                {t('license.scrive.noPeriodsDescription')}
              </EmptyState.Body>
            </EmptyState.Content>
            <EmptyState.Footer>
              <Button
                data-test-id="scrive-license-add-button-empty"
                leftIcon="add"
                text={t('license.scrive.addPeriod')}
                type="button"
                onClick={handleOpenAddModal}
                disabled={isDisabled}
              />
            </EmptyState.Footer>
          </EmptyState>
        </Center>
      )}
      {sortedScriveLicenses.length > 0 && (
        <Stack spacing={SIZE.S} alignItems="flex-end">
          <Button
            data-test-id="scrive-license-add-button"
            leftIcon="add"
            text={t('license.scrive.addPeriod')}
            type="button"
            onClick={handleOpenAddModal}
            disabled={isDisabled}
          />
        </Stack>
      )}

      <Modal open={deleteModal.open} onOpenChange={handleDeleteModalOpenChange}>
        <Modal.Header>
          <Modal.CloseButton
            ariaLabel={t('license.scrive.closeDeleteModal')}
            dataTestId="scrive-license-period-delete-modal-close"
          />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>
            {t('license.scrive.deletePeriodTitle')}
          </Modal.HeadlineText>
          <Stack spacing={SIZE.M}>
            <Text variant="smallRegular">
              {t('license.scrive.deletePeriodConfirm')}
            </Text>
            {deleteModal.period && (
              <Stack direction="row" spacing={SIZE.S} alignItems="center">
                <ScrivePeriodBadge
                  testId="scrive-period-delete-modal-badge"
                  status={deleteModal.period.status}
                />
                <Text variant="smallRegular">
                  {t('license.scrive.licensesCount', {
                    count: deleteModal.period.numLicenses,
                    range: formatDateRange(
                      deleteModal.period.dateFrom,
                      deleteModal.period.dateTo,
                    ),
                  })}
                </Text>
              </Stack>
            )}
            <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
              <Button
                data-test-id="scrive-license-period-delete-modal-cancel"
                text={t('common.cancel')}
                variant="text"
                type="button"
                onClick={() => handleDeleteModalOpenChange(false)}
              />
              <Button
                data-test-id="scrive-license-period-delete-modal-confirm"
                text={t('license.scrive.deletePeriod')}
                color="error"
                type="button"
                isLoading={isDeleting}
                disabled={isDisabled}
                onClick={handleDeleteConfirm}
              />
            </Stack>
          </Stack>
        </Modal.Content>
      </Modal>

      <Modal open={periodModal.open} onOpenChange={handlePeriodModalOpenChange}>
        <Modal.Header>
          <Modal.CloseButton
            ariaLabel={t('license.scrive.closePeriodModal', {
              action: t(
                isEditing
                  ? 'license.scrive.closePeriodModalEdit'
                  : 'license.scrive.closePeriodModalAdd',
              ),
            })}
            dataTestId="scrive-license-period-modal-close"
          />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>
            {isEditing
              ? t('license.scrive.editPeriod')
              : t('license.scrive.addPeriod')}
          </Modal.HeadlineText>
          <Form methods={formMethods} onSubmit={handlePeriodSubmit}>
            <Stack spacing={SIZE.M}>
              <Form.TextInput
                data-test-id="scrive-license-num-licenses-input"
                type="number"
                name="numLicenses"
                label={t('license.scrive.numLicenses')}
                required
                disabled={isDisabled}
                min={1}
              />
              <Form.DayInput
                data-test-id="scrive-license-date-from-input"
                name="dateFrom"
                mode="single"
                label={t('license.scrive.validFrom')}
                usePortal
                startMonth={dayPickerMonthRange.startMonth}
                endMonth={dayPickerMonthRange.endMonth}
                onChange={handleDateFromChange}
                dateFormatter={(date) =>
                  date ? formatDate(date, selectedLanguage) : ''
                }
              />
              <Form.DayInput
                data-test-id="scrive-license-date-to-input"
                name="dateTo"
                mode="single"
                label={t('license.scrive.validTo')}
                usePortal
                startMonth={dayPickerMonthRange.startMonth}
                endMonth={dayPickerMonthRange.endMonth}
                dateFormatter={(date) =>
                  date ? formatDate(date, selectedLanguage) : ''
                }
              />
              <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
                <Button
                  data-test-id="scrive-license-period-modal-cancel"
                  text={t('common.cancel')}
                  variant="text"
                  type="button"
                  onClick={() => handlePeriodModalOpenChange(false)}
                />
                <Form.SubmitButton
                  data-test-id="scrive-license-period-modal-submit"
                  text={
                    isEditing
                      ? t('license.scrive.saveChanges')
                      : t('license.scrive.addPeriod')
                  }
                  isLoading={isSubmitting}
                  disabled={isDisabled}
                />
              </Stack>
            </Stack>
          </Form>
        </Modal.Content>
      </Modal>
    </Stack>
  );
};

export default ScriveLicenses;
